import React, { useEffect } from "react";
import { Card, CardContent } from "../../components/ui/card";
import { DashboardContent } from "../../components/ui/metricsloadingstate";
import { Button } from "../../components/ui/button";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../store/store";
import HelpTopicCard from "../../dialogs/HelpTopics";
import { setEnrichmentDialogOpen } from "../../store/flowSlice";
import { useTranslation } from "../../i18n/hooks";

import {
  ArrowRight,
  Sparkles,
  CheckCircle,
  Loader2,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { useApi } from "../../hooks/useApi";
import ContentQualityActionCard from "../ProductsView/ContentSuggestionCard";
import { setInitialQualityScore } from "../../store/score/scoreSlice";
import getDashboardColorClasses from "../../utils/getDashboardColorClasses";
import ScoreVisual from "./ScoreVisuals";
import { extractShopName } from "./utils";
import MetricsView from "./MetricsView";


const PartiallyCompletedUser = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const api = useApi();
  const { currentCollection, currentUser } = useSelector(
    (state: RootState) => state.user
  );

  const storeName = extractShopName(
    currentCollection?.collection?.miqShopifyDomain ?? "",
    currentCollection?.collection?.name,
    currentCollection?.collection?.userId
  );

  const { data: productData, isLoading: productDataLoading } = api.useProducts({
    accountId: currentUser?.email ?? "",
    collectionName: currentCollection?.collection?.name ?? "",
    limit: 1,
    offset: 0,
    sortField: "title-asc",
    tableId: currentCollection?.collection?.tableId ?? "",
    filters: {
      quality_group: "all_products",
      tags: [],
      search: "",
      date_range: undefined,
      category_hierarchy: [],
      category_name: [],
      product_type: [],
    },
    metaFields: [],
  });

  const {
    industryAverage,
    averageQualityScore,
    qualityMessage,
    priorities,
    estimatedImpact,
  } = useSelector((state: RootState) => state.score);
  const { data, isLoading } = api.useQualityScore(
    currentCollection?.collection?.tableId!
  );
  const qualityScore = data?.data;

  useEffect(() => {
    if (qualityScore) {
      dispatch(setInitialQualityScore(qualityScore));
    }
  }, [qualityScore]);

  

  const overallColors = getDashboardColorClasses(averageQualityScore);

  return (
    <DashboardContent isLoading={isLoading || averageQualityScore === null}>
      <div className="w-full min-w-0">
        <div
          className={cn(
            "flex items-center justify-between gap-4 p-4 rounded-lg border mb-6",
            overallColors.lighter,
            overallColors.border
          )}
        >
          <div className="flex items-center gap-4">
            <div
              className={cn(
                "h-8 w-8 shrink-0 rounded-full flex items-center justify-center",
                overallColors.lighter
              )}
            >
              <CheckCircle className={cn("h-5 w-5", overallColors.text)} />
            </div>
            
              <div className="flex flex-col">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">{storeName}</span>
                  <span className="text-sm text-gray-400">•</span>
                  {productDataLoading ? (
                    <span className="text-sm text-gray-500 flex items-center gap-2">
                      <Loader2 className="h-3 w-3 animate-spin" />
                    </span>
                  ) : (
                    <span className="text-sm text-gray-500">
                      {
                        productData?.data?.content_quality_overview
                          ?.product_count
                      }{" "}
                      {t('dashboard.products')}
                    </span>
                  )}
                </div>
                <p className="text-gray-700 font-medium text-sm md:text-base">
                  {qualityMessage}
                </p>
              </div>
            
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">
          {/* Left Column - Main Metrics */}

          <div className="lg:col-span-8 space-y-6 md:space-y-8">
            {/* Main Score Card */}
            <Card className="relative overflow-hidden">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-8">
                  {/* Left side with gauge */}
                </div>

                <div className="flex flex-col md:flex-row gap-4 md:gap-0 items-center justify-between">
                  <div className="space-y-2">
                    <h1 className="text-2xl font-bold">
                      {t('dashboard.content_quality_score')}
                    </h1>

                    <p className="text-gray-500">
                      {t('dashboard.see_product_strength')}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-sm text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
                      {industryAverage}% {t('dashboard.industry_average')}
                    </div>
                    <div className="relative">
                      <ScoreVisual averageQualityScore={averageQualityScore ?? 0} />
                    </div>
                  </div>
                </div>

                {/* Main Metrics */}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                  {priorities.map((priority, index) => {
                    const colorClasses = getDashboardColorClasses(
                      priority.score
                    );
                    return (
                      <div
                        key={index}
                        className={cn("p-4 rounded-lg", colorClasses.lighter)}
                      >
                        <div className="flex items-center space-x-2 mb-2">
                          <div className={colorClasses.text}>
                            <priority.icon className="h-5 w-5" />
                          </div>
                          <h3 className="font-medium">
                            {priority.metricTitle}
                          </h3>
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span
                              className={cn(
                                "text-2xl font-bold",
                                colorClasses.text
                              )}
                            >
                              {priority.score}%
                            </span>
                          </div>
                          <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className={cn(
                                "h-full transition-all duration-500",
                                colorClasses.progress
                              )}
                              style={{ width: `${priority.score}%` }}
                            />
                          </div>
                          <p className={cn("text-sm", colorClasses.text)}>
                            {priority.message}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-4 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-6 text-white shadow-lg">
                  <div className="flex flex-col md:flex-row items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 bg-white/10 rounded-full flex items-center justify-center">
                        <Sparkles className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold">
                          {t('dashboard.ready_to_grow')}
                        </h3>
                        <p className="text-white/80">
                          {t('dashboard.potential_impact')}:{" "}
                          <span className="font-bold">{estimatedImpact}</span>{" "}
                          {t('dashboard.increase_in_conversions')}
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={() =>
                        dispatch(
                          setEnrichmentDialogOpen({
                            isOpen: true,
                            selectedFeatures: [
                              "title",
                              "description",
                              "seo",
                              "altText",
                              "productTags",
                            ],
                          })
                        )
                      }
                      data-testid="fix-all-issues-button"
                      className="bg-white mt-4 md:mt-0 text-blue-600 hover:bg-blue-50 hover:text-blue-700 font-semibold px-6 py-3 text-sm md:text-lg shadow-sm transition-all"
                    >
                      {t('dashboard.fix_all_issues')}
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </div>
              
                </div>
            
              </CardContent>
            </Card>
            <MetricsView />
          </div>

          <div className="lg:col-span-4 space-y-6 md:space-y-8">
            <ContentQualityActionCard
              tableId={currentCollection?.collection?.tableId!}
            />

            <HelpTopicCard />
          </div>
        </div>
      </div>
    </DashboardContent>
  );
};

export default PartiallyCompletedUser;
